import { useEffect } from 'react';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';

// Function to poll for image generation completion after refresh
const checkForImageGenerationCompletion = async (messageId, updateMessageStatus, STATUS_TYPES, setMessages) => {
  const pollInterval = setInterval(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const token = localStorage.getItem('token');
      
      const response = await authenticatedFetch(`${API_URL}/chat/message/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const message = await response.json();
        
        if (message.generatedImage && message.generatedImage.presignedUrl) {
          // Image generation is complete, stop polling
          console.log('🖼️ Image generation completed, stopping polling for:', messageId);
          clearInterval(pollInterval);
          
          // Clear the loading indicator
          updateMessageStatus(messageId, {
            type: STATUS_TYPES.COMPLETE,
            data: {}
          });
          
          // Update the message with the generated image
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            return currentMessages.map(msg => {
              if (msg._id === messageId) {
                return {
                  ...msg,
                  generatedImage: message.generatedImage,
                  text: '', // Clear loading indicator text
                  isGeneratingImage: false,
                  isComplete: true,
                  isTemporary: false
                };
              }
              return msg;
            });
          });
        } else if (!message.isGeneratingImage && !message.generatedImage) {
          // Generation failed or was cancelled, stop polling
          console.log('🖼️ Image generation failed or cancelled, stopping polling for:', messageId);
          clearInterval(pollInterval);
          
          updateMessageStatus(messageId, {
            type: STATUS_TYPES.COMPLETE,
            data: {}
          });
        }
      } else {
        // Message not found or error, stop polling
        console.log('🖼️ Image generation status check failed, stopping polling for:', messageId);
        clearInterval(pollInterval);
      }
    } catch (error) {
      console.warn('Error checking image generation completion:', error);
      clearInterval(pollInterval);
    }
  }, 2000); // Poll every 2 seconds
  
  // Stop polling after 5 minutes (safety measure for image generation)
  setTimeout(() => {
    clearInterval(pollInterval);
  }, 5 * 60 * 1000);
};

// Function to poll for superbucket completion after refresh
const checkForSuperbucketCompletion = async (messageId, updateMessageStatus, STATUS_TYPES, setMessages) => {
  const pollInterval = setInterval(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const token = localStorage.getItem('token');
      
      const response = await authenticatedFetch(`${API_URL}/chat/message/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const message = await response.json();
        
        if (!message.isQueryingSuperbucket && message.text !== '[[LOADING_INDICATOR]]' && message.isComplete) {
          // Superbucket query is complete, stop polling
          console.log('🗄️ Superbucket query completed, stopping polling for:', messageId);
          clearInterval(pollInterval);
          
          // Clear the loading indicator
          updateMessageStatus(messageId, {
            type: STATUS_TYPES.COMPLETE,
            data: {}
          });
          
          // Update the message with the superbucket response
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            return currentMessages.map(msg => {
              if (msg._id === messageId) {
                return {
                  ...msg,
                  text: message.text,
                  isQueryingSuperbucket: false,
                  isComplete: true,
                  isTemporary: false
                };
              }
              return msg;
            });
          });
        } else if (!message.isQueryingSuperbucket && !message.text) {
          // Query failed or was cancelled, stop polling
          console.log('🗄️ Superbucket query failed, stopping polling for:', messageId);
          clearInterval(pollInterval);
          
          updateMessageStatus(messageId, {
            type: STATUS_TYPES.COMPLETE,
            data: {}
          });
        }
      } else {
        // Message not found or error, stop polling
        console.log('🗄️ Superbucket query status check failed, stopping polling for:', messageId);
        clearInterval(pollInterval);
      }
    } catch (error) {
      console.warn('Error checking superbucket completion:', error);
      clearInterval(pollInterval);
    }
  }, 1000); // Poll every second for superbucket
  
  // Stop polling after 2 minutes (safety measure for superbucket queries)
  setTimeout(() => {
    clearInterval(pollInterval);
  }, 2 * 60 * 1000);
};

// Function to poll for deep research completion after refresh
const checkForDeepResearchCompletion = async (messageId, updateMessageStatus, STATUS_TYPES, setMessages) => {
  let lastKnownStage = null;
  let hasStartedStreaming = false;
  
  const pollInterval = setInterval(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const token = localStorage.getItem('token');
      
      const response = await authenticatedFetch(`${API_URL}/chat/deep-research-status/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        
        if (status.isDeepResearch && !status.isComplete && status.isTemporary) {
          // Update progress - be more conservative about stopping
          if (status.stage && status.progress !== undefined) {
            console.log('🧠 Updating deep research progress from polling:', {
              messageId,
              stage: status.stage,
              progress: status.progress,
              textLength: status.text?.length || 0
            });
            
            updateMessageStatus(messageId, {
              type: STATUS_TYPES.DEEP_RESEARCH,
              data: {
                stage: status.stage,
                progress: status.progress,
                message: status.text?.replace('🧠 ', '') || 'Processing deep research...'
              }
            });
            lastKnownStage = status.stage;
          }
        } else if (status.isComplete) {
          // Deep research is complete, stop polling
          console.log('🧠 Deep research completed, stopping polling for:', messageId);
          clearInterval(pollInterval);
          
          // Clear the loading indicator
          updateMessageStatus(messageId, {
            type: STATUS_TYPES.COMPLETE,
            data: {}
          });
          
          // Fetch the completed message content and update the messages directly
          try {
            const chatId = window.location.pathname.split('/chat/')[1];
            const messagesResponse = await authenticatedFetch(`${API_URL}/chat/${chatId}/messages`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (messagesResponse.ok) {
              const data = await messagesResponse.json();
              const updatedMessages = data.messages || [];
              
              console.log('🧠 Refreshed messages after deep research completion:', {
                messageId,
                messageCount: updatedMessages.length
              });
              
              // Update the messages to show the completed content
              setMessages(updatedMessages);
            }
          } catch (error) {
            console.error('Error refreshing messages after deep research completion:', error);
          }
        }
      } else {
        // Message not found or error, stop polling
        console.log('🧠 Deep research status check failed, stopping polling for:', messageId);
        clearInterval(pollInterval);
      }
    } catch (error) {
      console.warn('Error checking deep research completion:', error);
      clearInterval(pollInterval);
    }
  }, 3000); // Poll every 3 seconds
  
  // Stop polling after 10 minutes (safety measure)
  setTimeout(() => {
    clearInterval(pollInterval);
  }, 10 * 60 * 1000);
};

export const useMessageFetching = ({ chatId, setMessages, setIsLoading, setIsLoadingChat, setError, scrollToBottom, updateMessageStatus, STATUS_TYPES, sseStreamingActive }) => {
  // Fetch messages for existing chat
  useEffect(() => {
    if (!chatId) {
      // Immediately clear messages when navigating to new chat (chatId becomes null)
      setMessages([]);
      setIsLoadingChat(false);
      // Don't set loading to false here - might interfere with streaming
      setError(null);
      return;
    }
    
    // Set loading chat immediately (synchronously) so UI shows disabled send during fetch
    if (!sseStreamingActive?.current) {
      setIsLoadingChat(true);
    }
    
    const fetchMessages = async () => {
      try {
        // Only set loading state if we're not currently streaming
        if (!sseStreamingActive?.current) {
          setIsLoading(true);
        }
        setError(null);
        console.log('Fetching initial messages for chat:', chatId);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        // Use pagination endpoint - fetch most recent 30 messages (15 pairs)
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?limit=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch messages: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        const normalizeText = (text) => {
          if (typeof text === 'string') return text;
          if (text && typeof text === 'object') {
            if (typeof text.content === 'string') return text.content;
            if (Array.isArray(text)) return text.join(' ');
          }
          return '';
        };
        
        // Handle both old format (direct messages array) and new format (with pagination)
        const messagesList = data.messages || data || [];
        const paginationInfo = data.pagination || {};
        
        console.log('Fetched messages:', {
          chatId,
          messageCount: messagesList.length,
          hasMore: paginationInfo.hasMore,
          total: paginationInfo.total,
          messages: messagesList.map(m => ({ 
            id: m._id, 
            sender: m.sender, 
            textLength: m.text?.length || 0,
            isTemporary: m.isTemporary,
            isComplete: m.isComplete,
            isDeepResearch: m.isDeepResearch,
            metadata: m.metadata,
            text: normalizeText(m.text).substring(0, 50)
          }))
        });
        
        const messages = messagesList.map(msg => ({
          ...msg,
          text: normalizeText(msg.text)
        }));
        
        // Filter out any malformed messages
        const validMessages = messages.filter(msg => 
          msg && msg._id && msg.sender
        );
        
        if (validMessages.length !== messages.length) {
          console.warn('Filtered out malformed messages:', {
            original: messages.length,
            valid: validMessages.length
          });
        }
        
        // First-message flow: connect event fires before user message is persisted.
        // If we're actively streaming and fetch returns empty, preserve optimistic messages.
        if (sseStreamingActive?.current && validMessages.length === 0) {
          console.log('Skipping empty fetch - preserving optimistic messages during first-message stream');
          return;
        }

        // When actively streaming, MERGE fetch with current state — never overwrite toolCallPositions
        // or other streaming-only fields. Fetch has stale DB data (toolCallPositions: []) which would
        // wipe the tool indicators and show default loading instead.
        if (sseStreamingActive?.current && validMessages.length > 0) {
          setMessages(prev => {
            const current = Array.isArray(prev) ? prev : [];
            return validMessages.map(fetched => {
              const live = current.find(m => m._id === fetched._id);
              if (!live) return fetched;
              const hasStreamingToolState = (live.toolCallPositions?.length > 0) || live.toolCallsActive || live.hasToolCalls || live._codePreview;
              if (hasStreamingToolState) {
                return { ...fetched, ...live, toolCallPositions: live.toolCallPositions ?? fetched.toolCallPositions, toolCallsActive: live.toolCallsActive ?? fetched.toolCallsActive, hasToolCalls: live.hasToolCalls ?? fetched.hasToolCalls, _codePreview: live._codePreview, text: live.text ?? fetched.text, isStreaming: live.isStreaming ?? fetched.isStreaming };
              }
              return fetched;
            });
          });
        } else {
          setMessages(validMessages);
        }
        
        // Check for deep research messages that need progress indicators
        if (updateMessageStatus && STATUS_TYPES) {
          validMessages.forEach(msg => {
            if (msg.sender === 'bot' && msg.isDeepResearch && msg.metadata?.stage && msg.metadata?.progress && !msg.isComplete) {
              console.log('🧠 Initializing unified loading for deep research message:', {
                id: msg._id,
                stage: msg.metadata.stage,
                progress: msg.metadata.progress,
                textPreview: msg.text?.substring(0, 50)
              });
              updateMessageStatus(msg._id, {
                type: STATUS_TYPES.DEEP_RESEARCH,
                data: {
                  stage: msg.metadata.stage,
                  progress: msg.metadata.progress,
                  message: msg.text?.replace('🧠 ', '') || 'Processing deep research...'
                }
              });
              
              // Start listening for completion and updates via polling
              setTimeout(() => {
                checkForDeepResearchCompletion(msg._id, updateMessageStatus, STATUS_TYPES, setMessages);
              }, 1000);
            }
            
            // Check for image generation messages that need loading indicators
            if (msg.sender === 'bot' && msg.isGeneratingImage && msg.text === '[[LOADING_INDICATOR]]' && !msg.generatedImage && !msg.isComplete) {
              console.log('🖼️ Initializing unified loading for image generation message:', {
                id: msg._id,
                optimalSize: msg.optimalImageSize,
                textPreview: msg.text?.substring(0, 50)
              });
              updateMessageStatus(msg._id, {
                type: STATUS_TYPES.GENERATING_IMAGE,
                data: {
                  optimalSize: msg.optimalImageSize || 'auto'
                }
              });
              
              // Start polling for image completion
              setTimeout(() => {
                checkForImageGenerationCompletion(msg._id, updateMessageStatus, STATUS_TYPES, setMessages);
              }, 1000);
            }
            
            // Check for superbucket query messages that need loading indicators
            if (msg.sender === 'bot' && msg.isQueryingSuperbucket && msg.text === '[[LOADING_INDICATOR]]' && !msg.isComplete) {
              console.log('🗄️ Initializing unified loading for superbucket query message:', {
                id: msg._id,
                provider: msg.provider,
                textPreview: msg.text?.substring(0, 50)
              });
              updateMessageStatus(msg._id, {
                type: STATUS_TYPES.SUPERBUCKET_QUERY,
                data: {}
              });
              
              // Start polling for superbucket completion
              setTimeout(() => {
                checkForSuperbucketCompletion(msg._id, updateMessageStatus, STATUS_TYPES, setMessages);
              }, 1000);
            }
          });
        }
        
        // Check for any incomplete messages and mark them appropriately
        // Skip when actively streaming — we'd overwrite toolCallPositions and break tool indicators
        const hasIncompleteMessages = !sseStreamingActive?.current && validMessages.some(msg => 
          msg.sender === 'bot' && 
          (msg.isTemporary || msg.text === '[[LOADING_INDICATOR]]' || !msg.isComplete)
        );
        
        if (hasIncompleteMessages) {
          console.warn('Found incomplete messages, checking age...');
          
          // Only clean up messages that have been loading for more than 10 seconds
          const cleanedMessages = validMessages.map(msg => {
            if (msg.sender === 'bot' && 
                (msg.isTemporary || msg.text === '[[LOADING_INDICATOR]]' || !msg.isComplete)) {
              
              // Don't mark deep research messages as stuck - they can take several minutes
              if (msg.isDeepResearch || msg.text?.includes('🧠')) {
                console.log('🧠 Keeping deep research message, allowing more time:', {
                  id: msg._id,
                  isDeepResearch: msg.isDeepResearch,
                  hasMetadata: !!msg.metadata,
                  metadata: msg.metadata,
                  textPreview: msg.text?.substring(0, 50)
                });
                return msg;
              }
              
              // Don't mark image generation messages as stuck - they can take several minutes
              if (msg.isGeneratingImage || msg.text?.includes('Creating image')) {
                console.log('🖼️ Keeping image generation message, allowing more time:', {
                  id: msg._id,
                  isGeneratingImage: msg.isGeneratingImage,
                  optimalImageSize: msg.optimalImageSize,
                  textPreview: msg.text?.substring(0, 50)
                });
                return msg;
              }
              
              // Check how long this message has been loading
              const messageAge = Date.now() - new Date(msg.createdAt || msg.timestamp).getTime();
              const isStuckMessage = messageAge > 10000; // 10 seconds
              
              if (isStuckMessage) {
                console.warn('Marking stuck message as interrupted:', msg._id, 'Age:', messageAge + 'ms');
                return {
                  ...msg,
                  text: msg.text === '[[LOADING_INDICATOR]]' ? 
                    "This message was interrupted. Please try sending your message again." : 
                    msg.text,
                  isTemporary: false,
                  isComplete: true,
                  isError: true
                };
              } else {
                console.log('Keeping loading message, still fresh:', msg._id, 'Age:', messageAge + 'ms');
                // Keep the message as-is if it's still fresh
                return msg;
              }
            }
            return msg;
          });
          setMessages(cleanedMessages);
        }
        
      } catch (error) {
        console.error('Error fetching messages:', error);
        setError(`Failed to load messages: ${error.message}`);
        
        // If we can't load messages, at least clear the loading state
        setMessages([]);
      } finally {
        setIsLoadingChat(false);
        // Only clear loading state if we're not currently streaming
        if (!sseStreamingActive?.current) {
          setIsLoading(false);
        }
      }
    };
    
    fetchMessages();
  }, [chatId, scrollToBottom, setMessages, setIsLoading, setIsLoadingChat, setError]);
}; 